import PropTypes from 'prop-types';
import Joi from 'joi';
import Form from '../../common/form';
import { MATERIAL_OPTIONS } from '../../../utils/constants';
import { getAffirmativeOptions } from '../../../utils/data';
import toastService from '../../../services/toastService';

export interface LRUTGeneralInformation {
    technologyIntroduction: string;
    material: string;
    fluidType: string;
    nominalThickness: number;
    corrosionAllowance: number;
    outerDiameter: number;
    dateOfInspection: Date;
    calibrationDueDate: Date;
    guidedWaveTechnology: string;
    equipmentSN: string;
    cableCheck: string;
    collarSize: number;
    collarSN: string;
}

class LRUTGeneralInformationForm extends Form {
    state = {
		data: {
			technologyIntroduction: '',
            material: '',
            fluidType: '',
            nominalThickness: 0,
            corrosionAllowance: 0,
            outerDiameter: 0,
			dateOfInspection: new Date(),
			calibrationDueDate: new Date(),
            guidedWaveTechnology: '',
            equipmentSN: '',
            cableCheck: '',
            collarSize: 0,
            collarSN: '',
		},
		errors: {},
		loading: false,
		progress: 0,
	};

    static propTypes = {
        initialLRUTData: PropTypes.object,
        onProceed: PropTypes.func,
    };
    
    schema = {
        technologyIntroduction: Joi.string().min(5).required().label('Scope Of Work'),
        material: Joi.string().min(3).required().label('Material'),
        fluidType: Joi.string().min(3).required().label('Fluid Type'),
        nominalThickness: Joi.number().positive().required().label('Nominal Thickness (mm)'),
        corrosionAllowance: Joi.number().positive().required().label('Corrosion Allowance (mm)'),
        outerDiameter: Joi.number().positive().required().label('Outer Diameter (mm)'),
        dateOfInspection: Joi.date().less('now').required().label('Date of Inspection'),
        calibrationDueDate: Joi.date().greater('now').required().label('Calibration Due Date'),
        guidedWaveTechnology: Joi.string().min(3).required().label('Guided Wave Technology'),
        equipmentSN: Joi.string().min(3).required().label('Equipment Serial Number'),
        cableCheck: Joi.string().min(3).required().label('Cable Check'),
        collarSize: Joi.number().positive().required().label('Collar Size'),
        collarSN: Joi.string().min(3).required().label('Collar Serial Number')
    };

    componentDidMount(): void {
        if (this.props.initialLRUTData) {
            this.setState({ data: this.props.initialLRUTData });
        }
    }

    doSubmit = () => {
        this.props.onProceed(this.state.data);
		toastService.success('General Information saved successfully!');
    };

    render() { 
        return ( 
            <form onSubmit={this.handleSubmit}>
                {this.renderTextArea({
					name: 'technologyIntroduction',
					label: '',
					rows: '4',
					placeholder: 'Enter Technology Introduction',
				})}

                <div className="divider my-4"></div>

                <div className="container-fluid">
                    <div className="row">
                        <div className="col">
							{this.renderDatePicker({
								name: 'dateOfInspection',
								label: 'Date of Inspection',
								placeholderText: 'Select Date of Inspection',
								maxDate: new Date(),
							})}
						</div>

                        <div className="col">
                            {this.renderSelect({
                                name: 'material',
                                options: MATERIAL_OPTIONS,
                                label: 'Material',
                                placeholder: 'Select Material'
                            })}
                        </div>

                        <div className="col">
                            {this.renderInput({
                                type: 'text',
                                name: 'fluidType',
                                label: 'Fluid Type',
                                placeholder: 'Enter Fluid Type'
                            })}
                        </div>
                    </div>

                    <div className="my-4 divider"></div>

                    <div className="row">
                        <div className="col">
                            {this.renderInput({
                                type: 'text',
                                name: 'nominalThickness',
                                label: 'Nominal Thickness (mm)',
                                placeholder: 'Select Nominal Thickness'
                            })}
                        </div>

                        <div className="col">
                            {this.renderInput({
                                type: 'text',
                                name: 'corrosionAllowance',
                                label: 'Corrosion Allowance (mm)',
                                placeholder: 'Select Corrosion Allowance'
                            })}
                        </div>

                        <div className="col">
                            {this.renderInput({
                                type: 'text',
                                name: 'outerDiameter',
                                label: 'Outer Diameter (mm)',
                                placeholder: 'Select Outer Diameter'
                            })}
                        </div>
                    </div>

                    <div className="my-4 divider"></div>

                    <div className="row">
                        <div className="col">
                            {this.renderSelect({
                                name: 'guidedWaveTechnology',
                                options: WAVE_TECHNOLOGIES,
                                label: 'Guided Wave Technology',
                                placeholder: 'Select Guided Wave Technology'
                            })}
                        </div>

                        <div className="col">
                            {this.renderInput({
                                type: 'text',
                                name: 'equipmentSN',
                                label: 'Equipment Serial Number',
                                placeholder: 'Enter Equipment Serial Number'
                            })}
                        </div>

                        <div className="col">
							{this.renderDatePicker({
								name: 'calibrationDueDate',
								label: 'Calibration Due Date',
								placeholderText: 'Select Calibration Due Date',
								minDate: new Date(),
							})}
						</div>

                        <div className="col">
                            {this.renderSelect({
                                name: 'cableCheck',
                                options: getAffirmativeOptions(),
                                label: 'Cable Check',
                                placeholder: 'Select Cable Check'
                            })}
                        </div>
                    </div>

                    <div className="my-4 divider"></div>

                    <div className="row">
                        <div className="col">
                            {this.renderSelect({
                                name: 'collarSize',
                                options: COLLAR_SIZES,
                                label: 'Collar Size (inch)',
                                placeholder: 'Select Collar Size'
                            })}
                        </div>
                        
                        <div className="col">
                            {this.renderInput({
                                type: 'text',
                                name: 'collarSN',
                                label: 'Collar Serial Number',
                                placeholder: 'Enter Collar Serial Number'
                            })}
                        </div>
                    </div>
                </div>

                <div className="divider mt-4 mb-2"></div>

				<div className="d-flex justify-content-end">
					{this.renderButton('Save General Info')}
				</div>
            </form>
         );
    }
}
 
const COLLAR_SIZES = [
    {
        id: 0.75,
        name: 0.75,
    },
    {
        id: 1,
        name: 1,
    },
    {
        id: 1.5,
        name: 1.5,
    },
    {
        id: 2,
        name: 2,
    },
    {
        id: 4,
        name: 4,
    },
    {
        id: 6,
        name: 6,
    },
    {
        id: 8,
        name: 8,
    },
    {
        id: 10,
        name: 10,
    },
    {
        id: 12,
        name: 12,
    },
    {
        id: 14,
        name: 14,
    },
    {
        id: 16,
        name: 16,
    },
    {
        id: 18,
        name: 18,
    },
    {
        id: 20,
        name: 20,
    },
    {
        id: 22,
        name: 22,
    },
    {
        id: 24,
        name: 24,
    },
    {
        id: 26,
        name: 26,
    },
    {
        id: 28,
        name: 28,
    },
    {
        id: 30,
        name: 30,
    },
    {
        id: 32,
        name: 32,
    },
    {
        id: 34,
        name: 34,
    },
    {
        id: 36,
        name: 36,
    },
    {
        id: 38,
        name: 38,
    },
    {
        id: 40,
        name: 40,
    },
    {
        id: 42,
        name: 42,
    },
    {
        id: 44,
        name: 44,
    },
    {
        id: 46,
        name: 46,
    },
    {
        id: 48,
        name: 48,
    },
    {
        id: 50,
        name: 50,
    },
];

const WAVE_TECHNOLOGIES = [
    {
        id: 'G3',
        name: 'G3',
    },
    {
        id: 'G4',
        name: 'G4',
    },
    {
        id: 'G4M',
        name: 'G4M',
    },
];

export default LRUTGeneralInformationForm;